import { BulkActionsToolbar } from '@/components/ra-lists/BulkActionsToolbar';
import { DatagridContextProvider } from '@/components/ra-lists/Datagrid/DatagridContextProvider';
import { Table } from '@mui/material';
import clsx from 'clsx';
import difference from 'lodash/difference';
import union from 'lodash/union';
import { Identifier, sanitizeListRestProps, useListContext, useResourceContext, useTranslate } from 'ra-core';
import { FC, cloneElement, createElement, isValidElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as React from 'react';
import {
  BulkDeleteButton,
  DatagridBody,
  DatagridClasses,
  DatagridLoading,
  DatagridRoot,
  PureDatagridBody,
  DatagridProps as RaDatagridProps
} from 'react-admin';
import { Empty } from '@/components/ra-lists/Empty';
import { DatagridHeader, SortItem } from './DatagridHeader';

const defaultBulkActionButtons = <BulkDeleteButton />;

/**
 * The Datagrid component renders a list of records as a table.
 * It is usually used as a child of the <List> and <ReferenceManyField> components.
 *
 * Props:
 *  - body
 *  - bulkActionButtons
 *  - children
 *  - empty
 *  - expand
 *  - header
 *  - hover
 *  - isRowExpandable
 *  - isRowSelectable
 *  - optimized
 *  - rowClick
 *  - rowSx
 *  - size
 *  - sx
 *
 * @example // Display all posts as a datagrid
 * const postRowSx = (record, index) => ({
 *     backgroundColor: record.nb_views >= 500 ? '#efe' : 'white',
 * });
 * export const PostList = () => (
 *     <List>
 *         <Datagrid rowSx={postRowSx}>
 *             <TextField source="id" />
 *             <TextField source="title" />
 *             <TextField source="body" />
 *             <EditButton />
 *         </Datagrid>
 *     </List>
 * );
 *
 * @example // Display all the comments of the current post as a datagrid
 * <ReferenceManyField reference="comments" target="post_id">
 *     <Datagrid>
 *         <TextField source="id" />
 *         <TextField source="body" />
 *         <DateField source="created_at" />
 *         <EditButton />
 *     </Datagrid>
 * </ReferenceManyField>
 *
 * @example // Usage outside of a <List> or a <ReferenceManyField>.
 *
 * const sort = { field: 'published_at', order: 'DESC' };
 *
 * export const MyCustomList = (props) => {
 *     const { data, total, isLoading } = useGetList(
 *         'posts',
 *         { pagination: { page: 1, perPage: 10 }, sort: sort }
 *     );
 *
 *     return (
 *         <Datagrid
 *             data={data}
 *             total={total}
 *             isLoading={isLoading}
 *             sort={sort}
 *             selectedIds={[]}
 *             setSort={() => {
 *                 console.log('set sort');
 *             }}
 *             onSelect={() => {
 *                 console.log('on select');
 *             }}
 *             onToggleItem={() => {
 *                 console.log('on toggle item');
 *             }}
 *         >
 *             <TextField source="id" />
 *             <TextField source="title" />
 *         </Datagrid>
 *     );
 * }
 */
const Datagrid = React.forwardRef((props, ref) => {
  const {
    optimized = false,
    body = optimized ? PureDatagridBody : DatagridBody,
    header = DatagridHeader,
    children,
    className,
    empty,
    expand,
    bulkActionsToolbar = false,
    bulkActionButtons = defaultBulkActionButtons,
    hover,
    isRowSelectable,
    isRowExpandable,
    resource: resourceProp,
    rowClick,
    rowSx,
    rowStyle,
    size = 'small',
    sx,
    expandSingle = false,
    ...rest
  } = props;

  const translate = useTranslate();
  const listContext = useListContext(props);
  const { sort, data, isLoading, onSelect, onToggleItem, selectedIds, setSort, total } = listContext;
  const resourceContext = useResourceContext();
  const resource = resourceProp ?? resourceContext;
  const navigate = useNavigate();
  const location = useLocation();

  const clickedFields = useRef<Set<string>>(new Set());

  function handleHeaderSortClick(field: string) {
    if (field) clickedFields.current.add(field);
  }

  const multiSort = useMemo<SortItem[]>(() => {
    if (!sort || !sort.field) return [];
    if (sort.field === 'id' && !clickedFields.current.has('id')) {
      return [];
    }
    const fields = sort.field.split(',');
    const orders = (sort.order || '').split(',');
    return fields.map((field, i) => ({
      field,
      order: (orders[i] || 'ASC') as 'ASC' | 'DESC'
    }));
  }, [sort]);

  const onMultiSortChange = useCallback(
    (newMultiSort: SortItem[]) => {
      const newField = newMultiSort.map((s) => s.field).join(',');
      const newOrder = newMultiSort.length > 0 ? newMultiSort.map((s) => s.order).join(',') : 'ASC';

      if (newMultiSort.length > 1) {
        const searchParams = new URLSearchParams(
          location.search.startsWith('?') ? location.search.slice(1) : location.search
        );

        searchParams.set('sort', newField);
        searchParams.set('order', newOrder);

        const url = searchParams.toString();
        navigate(`${location.pathname}${url ? `?${url}` : ''}`, { replace: true });
      } else {
        setSort({
          field: newField,
          order: newOrder as 'ASC' | 'DESC'
        });
      }
    },
    [location, navigate, setSort]
  );

  const hasBulkActions = !!bulkActionButtons !== false;
  const contextValue = useMemo(() => ({ isRowExpandable, expandSingle }), [isRowExpandable, expandSingle]);
  const lastSelected = useRef(null);

  useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) {
      lastSelected.current = null;
    }
  }, [JSON.stringify(selectedIds)]); // eslint-disable-line react-hooks/exhaustive-deps

  // we manage row selection at the datagrid level to allow shift+click to select an array of rows
  const handleToggleItem = useCallback(
    // @ts-ignore
    (id, event) => {
      const ids = data.map((record) => record.id);
      const lastSelectedIndex = ids.indexOf(lastSelected.current);
      lastSelected.current = event.target.checked ? id : null;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1
        );

        const newSelectedIds = event.target.checked
          ? union(selectedIds, idsBetweenSelections)
          : difference(selectedIds, idsBetweenSelections);

        onSelect(
          isRowSelectable
            ? newSelectedIds.filter((id: Identifier) => isRowSelectable(data.find((record) => record.id === id)))
            : newSelectedIds
        );
      } else {
        onToggleItem(id);
      }
    },
    [data, isRowSelectable, onSelect, onToggleItem, selectedIds]
  );

  const DefaultEmpty = (
    <Empty
      hasCreate={false}
      sx={{ '& .ApplicaEmpty-icon': { fontSize: '7em' } }}
      title={translate('ra.navigation.no_results')}
    />
  );

  if (isLoading === true) {
    return (
      <DatagridLoading
        className={className}
        expand={expand}
        hasBulkActions={hasBulkActions}
        nbChildren={React.Children.count(children)}
        size={size}
      />
    );
  }

  /**
   * Once loaded, the data for the list may be empty. Instead of
   * displaying the table header with zero data rows,
   * the Datagrid displays the empty component.
   */
  if (data == null || data.length === 0 || total === 0) {
    return empty || DefaultEmpty;
  }

  /**
   * After the initial load, if the data for the list isn't empty,
   * and even if the data is refreshing (e.g. after a filter change),
   * the datagrid displays the current data.
   */
  return (
    <DatagridContextProvider value={contextValue}>
      <DatagridRoot sx={sx} className={clsx(DatagridClasses.root, className)}>
        {bulkActionsToolbar != null && isValidElement(bulkActionsToolbar) ? (
          cloneElement<any>(bulkActionsToolbar, {
            selectedIds,
            total
          })
        ) : bulkActionButtons !== false ? (
          <BulkActionsToolbar>
            {isValidElement(bulkActionButtons) ? bulkActionButtons : defaultBulkActionButtons}
          </BulkActionsToolbar>
        ) : null}
        <div className={DatagridClasses.tableWrapper}>
          <Table ref={ref} className={DatagridClasses.table} size={size} {...sanitizeRestProps(rest)}>
            {createOrCloneElement(
              <DatagridHeader multiSort={multiSort} setMultiSort={onMultiSortChange} />,
              {
                children,
                sort,
                data,
                hasExpand: !!expand,
                hasBulkActions,
                isRowSelectable,
                onSelect,
                resource,
                selectedIds,
                setSort,
                onSortClick: handleHeaderSortClick
              },
              children
            )}
            {createOrCloneElement(
              body,
              {
                expand,
                rowClick,
                data,
                hasBulkActions,
                hover,
                onToggleItem: handleToggleItem,
                resource,
                rowSx,
                rowStyle,
                selectedIds,
                isRowSelectable
              },
              children
            )}
          </Table>
        </div>
      </DatagridRoot>
    </DatagridContextProvider>
  );
}) as FC<DatagridProps>;

type DatagridProps = RaDatagridProps &
  React.RefAttributes<HTMLTableElement> & {
    /**
     * An alternative to bulkActionButtons, to be used when the actions are too complex to be expressed.
     * In this case you are free to fully control the rendering of the bulk actions toolbar.
     * @see BulkActionsToolbar
     * @example <caption>Using the BulkActionsToolbar component</caption>
     * import { BulkActionsToolbar } from '@applica-software-guru/react-admin';
     * import { BulkDeleteButton } from 'react-admin';
     *
     * const PostBulkActionButtons = props => (
     *    <BulkActionsToolbar {...props}>
     *       <BulkDeleteButton />
     *      // add your custom actions here
     *   </BulkActionsToolbar>
     * );
     *
     * @example <caption>Using BulkFloatingActionsToolbar component</caption>
     * import { BulkActionsToolbar } from '@applica-software-guru/react-admin';
     * import { BulkDeleteButton } from 'react-admin';
     *
     * const PostBulkActionButtons = props => (
     *   <BulkActionsToolbar {...props}>
     *      <BulkDeleteButton />
     *     // add your custom actions here
     *   </BulkActionsToolbar>
     * );
     *
     */
    bulkActionsToolbar: React.ReactNode | React.FC | boolean;
  };

// @ts-ignore
function createOrCloneElement(element, props, children) {
  return isValidElement(element) ? cloneElement(element, props, children) : createElement(element, props, children);
}

const injectedProps = [
  'isRequired',
  'setFilter',
  'setPagination',
  'limitChoicesToValue',
  'translateChoice',
  // Datagrid may be used as an alternative to SelectInput
  'field',
  'fieldState',
  'formState'
];

// @ts-ignore
function sanitizeRestProps(props) {
  return Object.keys(sanitizeListRestProps(props))
    .filter((propName) => !injectedProps.includes(propName))
    .reduce((acc, key) => ({ ...acc, [key]: props[key] }), {});
}
Datagrid.displayName = 'Datagrid';

export { Datagrid };
export type { DatagridProps };
